import { useCallback } from 'react'
import { ContentItemViewNodesType } from '../../../../../types/contentItemViewNode.type'
import { UpdatedTrackChangesData } from '../../../hooks'
import { useTreeMutations } from '../../../hooks/TreeMutations/useTreeMutations'
import { useTrackChangesUpdate } from './useTrackChangesUpdate'

/**
 * A custom hook that provides tree mutation utilities with track changes support.
 * This hook enhances the existing tree mutation functionality by integrating track changes updates.
 * @returns {Object} An object containing the `updateTreeViewNodes` function.
 */
export const useTreeMutationsWithTrackChanges = () => {
    const { reRenderTree } = useTreeMutations()
    const { 
        handleUpdateAfterAcceptRejectTrackChanges,
        updateTrackChanges, 
        isServiceAvailable 
    } = useTrackChangesUpdate()

    const updateTreeViewNodes = useCallback(
        (
            updatedNodes: Partial<ContentItemViewNodesType>[],
            updatedTrackChanges?: UpdatedTrackChangesData
        ) => {
            const trackChangesUpdateFn =
                isServiceAvailable && !updatedTrackChanges
                    ? updateTrackChanges
                    : undefined

            reRenderTree({
                updatedContentItemViewNodes: updatedNodes,
                shouldSetIsSuggestionsDataUpdated: false,
                trackChangesUpdateFunction: trackChangesUpdateFn,
            })

            if (updatedTrackChanges) {
                const { trackChangesData, action } = updatedTrackChanges
                handleUpdateAfterAcceptRejectTrackChanges(
                    trackChangesData,
                    action
                )
            }
        },
        [
            reRenderTree,
            updateTrackChanges,
            isServiceAvailable,
            handleUpdateAfterAcceptRejectTrackChanges,
        ]
    )

    return { updateTreeViewNodes }
}
